import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Cookie } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { Link } from "react-router-dom";

const KEY = "mss_cookie_consent";
const PREF = "mss_cookie_prefs";

const updateGtagConsent = (prefs) => {
  if (typeof window.gtag !== "function") return;
  window.gtag("consent", "update", {
    ad_storage: prefs.marketing ? "granted" : "denied",
    ad_user_data: prefs.marketing ? "granted" : "denied",
    ad_personalization: prefs.marketing ? "granted" : "denied",
    analytics_storage: prefs.analytics ? "granted" : "denied",
    functionality_storage: "granted",
    personalization_storage: prefs.analytics ? "granted" : "denied",
    security_storage: "granted",
  });
};

const persist = (mode, prefs) => {
  try {
    localStorage.setItem(KEY, mode);
    localStorage.setItem(PREF, JSON.stringify(prefs));
    updateGtagConsent(prefs);
    window.dispatchEvent(new CustomEvent("mss-consent-updated", { detail: prefs }));
  } catch (e) {}
};

export default function CookieBanner() {
  const [show, setShow] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState({ necessary: true, analytics: false, marketing: false });

  useEffect(() => {
    try {
      const consent = localStorage.getItem(KEY);
      const saved = JSON.parse(localStorage.getItem(PREF) || "null");
      if (!consent) {
        setShow(true);
        // Prévient le bouton d'appel de se cacher
        window.dispatchEvent(new CustomEvent("mss-banner-visibility", { detail: true }));
      } else if (saved) {
        setPrefs(saved);
        updateGtagConsent(saved);
      }
    } catch (e) {
      setShow(true);
    }
  }, []);

  useEffect(() => {
    // Émet la visibilité pour que le bouton d'appel s'adapte
    window.dispatchEvent(new CustomEvent("mss-banner-visibility", { detail: show }));
  }, [show]);

  const acceptAll = () => {
    const p = { necessary: true, analytics: true, marketing: true };
    setPrefs(p);
    persist("all", p);
    setShow(false);
  };

  const rejectAll = () => {
    const p = { necessary: true, analytics: false, marketing: false };
    setPrefs(p);
    persist("none", p);
    setShow(false);
  };

  const save = () => {
    persist("custom", prefs);
    setShowSettings(false);
    setShow(false);
  };

  if (!show) return null;

  return (
    <>
      <div
        className="fixed bottom-0 left-0 right-0 z-50 p-3 md:p-6"
        role="dialog"
        aria-label="Bandeau de gestion des cookies"
        style={{ paddingBottom: "max(env(safe-area-inset-bottom, 0px), 0.75rem)" }}
      >
        <div className="container mx-auto max-w-4xl">
          <div className="bg-card border border-border rounded-2xl shadow-2xl p-5 md:p-8">
            {/* Version mobile — épurée et gros boutons */}
            <div className="md:hidden">
              <div className="flex items-start gap-3 mb-5">
                <Cookie className="w-7 h-7 text-primary flex-shrink-0 mt-0.5" aria-hidden="true" />
                <div>
                  <h3 className="text-lg font-bold text-foreground mb-1">Cookies</h3>
                  <p className="text-sm text-muted-foreground leading-snug">
                    Ce site utilise des cookies pour améliorer votre expérience.{" "}
                    <Link to="/cookies" className="underline text-foreground font-medium">En savoir plus</Link>
                  </p>
                </div>
              </div>
              <div className="space-y-3">
                <Button
                  onClick={acceptAll}
                  className="w-full h-14 text-lg font-bold shadow-lg active:scale-[0.98] transition-transform"
                >
                  Tout accepter
                </Button>
                <Button
                  onClick={rejectAll}
                  variant="outline"
                  className="w-full h-14 text-lg font-semibold border-2 active:scale-[0.98] transition-transform"
                >
                  Tout refuser
                </Button>
                <button
                  onClick={() => setShowSettings(true)}
                  className="w-full text-sm text-muted-foreground hover:text-foreground underline py-3 transition-colors"
                >
                  Personnaliser mes choix
                </button>
              </div>
            </div>

            {/* Version desktop — inchangée */}
            <div className="hidden md:flex items-start gap-4">
              <Cookie className="w-6 h-6 text-primary flex-shrink-0" aria-hidden="true" />
              <div className="flex-1 space-y-4">
                <div>
                  <h3 className="text-lg font-bold text-foreground mb-2">Gestion des cookies</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Nous utilisons des cookies pour améliorer votre expérience, analyser le trafic du site et personnaliser le contenu.
                    Vous pouvez accepter, refuser ou personnaliser vos préférences. Consultez notre{" "}
                    <Link to="/cookies" className="underline hover:text-foreground">politique de cookies</Link>.
                  </p>
                </div>
                <div className="flex gap-3">
                  <Button onClick={acceptAll} size="sm" className="flex-1">Tout accepter</Button>
                  <Button onClick={rejectAll} variant="outline" size="sm" className="flex-1">Tout refuser</Button>
                  <Button onClick={() => setShowSettings(true)} variant="ghost" size="sm" className="flex-1">Personnaliser</Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Préférences de cookies</DialogTitle>
            <DialogDescription>Gérez vos préférences. Les cookies nécessaires ne peuvent pas être désactivés.</DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {[
              { id: "necessary", label: "Cookies nécessaires", desc: "Essentiels au fonctionnement du site (session, sécurité)", disabled: true },
              { id: "analytics", label: "Cookies d'analyse", desc: "Nous aident à comprendre l'utilisation du site (statistiques anonymisées)" },
              { id: "marketing", label: "Cookies marketing", desc: "Utilisés pour mesurer et personnaliser nos campagnes publicitaires" },
            ].map((c) => (
              <div key={c.id} className="flex items-center justify-between">
                <div className="space-y-0.5 pr-4">
                  <Label htmlFor={c.id} className="text-base font-semibold">{c.label}</Label>
                  <p className="text-sm text-muted-foreground">{c.desc}</p>
                </div>
                <Switch id={c.id} checked={prefs[c.id]} disabled={c.disabled} onCheckedChange={(v) => setPrefs({ ...prefs, [c.id]: v })} />
              </div>
            ))}
          </div>
          <div className="flex gap-3 pt-4">
            <Button onClick={save} className="flex-1 h-12">Enregistrer mes préférences</Button>
            <Button onClick={() => setShowSettings(false)} variant="outline" className="flex-1 h-12">Annuler</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
